import { SizeMorphDirective } from '../_card-base/size-morph.directive';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DocumentItemPartial, FavoriteFieldEntity } from '@domain';
import { environment } from '@environments';
import { TranslatePipe } from '@ngx-translate/core';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { CardDescriptionAreaComponent } from '../_card-base/card-description-area.component';
import { CardTypes } from '../_card-base/enums/card-types.enum';
import { DynamicActionModel } from '../_card-base/model/dynamic-action.model';

import { MediaLookChipComponent } from '../_card-base/media-look-chip/media-look-chip.component';
import {
    hasAnyKey,
    withCaptionSide,
    withCaptionStyle,
    withMediaFill,
    withOverride,
    withoutKeys,
} from '../_card-base/item-look.util';
import { MEDIA_FILL_DEFAULT, resolveMediaFill } from '../../../../../media-fill-options';
import {
    CaptionStyle,
    resolveCaptionSide,
    resolveCaptionStyle,
} from '../../../../../pdf-renderer/caption-style';
import { ImageSize } from '../image-card/image-size.util';

@Component({
    selector: 'video-card',
    templateUrl: 'video-card.component.html',
    styleUrl: 'video-card.component.scss',
    imports: [
        SizeMorphDirective,
        MediaLookChipComponent,
        CardBaseComponent,
        CardDescriptionAreaComponent,
        TranslatePipe,
    ],
})
export class VideoCardComponent implements OnInit {
    public field = input<DocumentItemPartial>();
    public fontFamily = input<string>(environment.DEFAULT_FONT_FAMILY);
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);

    public editDataService = inject(EditDataService);
    public editItemsService = inject(EditItemsService);

    /** Painel "Foto"/"Grade"/"Vídeo" do rail: é ele que a prévia abre. */
    private _lookChip = viewChild(MediaLookChipComponent);
    public videoActions: DynamicActionModel[] = [];

    constructor() {
        /*
         * A prévia pede as configurações deste item pelo menu que ela mostra em cima dele.
         * Abre o MESMO painel do botão do rail (cada card tem o seu), então o pedido chega
         * por um signal e o card dono abre o painel dele, e limpa o pedido, senão voltar
         * para a seção mais tarde reabriria o painel do nada.
         *
         * O painel só existe depois que a view do card monta: até lá o pedido espera, e o
         * effect roda de novo quando o `viewChild` resolve.
         */
        effect(() => {
            const requested = this.editDataService.itemConfigRequestId();
            if (!requested || requested !== this.field()?._id) return;

            const lookChip = this._lookChip();
            if (!lookChip) return;

            untracked(() => {
                lookChip.open();
                this.editDataService.clearItemConfigRequest();
            });
        });
    }

    ngOnInit(): void {
        // As configurações (tamanho, enquadramento e mostrar no PDF) viraram o botão "Vídeo"
        // do rail, que a prévia também abre.
        this.videoActions = [];
    }

    /** Chaves do menu "Vídeo" que o card pode sobrescrever. */
    private static readonly LOOK_KEYS = [
        'singleVideoGrid',
        'fullImage',
        'showVideo',
        'captionStyle',
        'captionPosition',
        'imageFillColor',
    ] as const;

    /** Cor do fundo ao vivo, enquanto o seletor livre do painel é arrastado. */
    private _liveFill = signal<string | null>(null);

    /** Cor do fundo efetiva (card, documento), como no PDF; a ao vivo por cima. */
    public fillColor = computed(
        () =>
            this._liveFill() ??
            resolveMediaFill([
                this.getCurrentField()?.configs,
                this.editDataService.document()?.config,
            ])
    );

    /**
     * A var `--media-fill` do card: só com cor escolhida. Sem ela a sobra fica no cinza do
     * tema (--backgroundSecondary), que no tema claro é o mesmo cinza padrão do PDF.
     */
    /**
     * A faixa da cor do fundo aparece em volta do player reduzido (Média, Pequena), como na
     * foto única e no PDF. Ao lado da descrição a coluna é do player, sem faixa.
     */
    public banded = computed(() => !this.captionSide() && this.videoSize().grid > 1);

    public fillStyle = computed(() => {
        const color = this.fillColor();
        return color === MEDIA_FILL_DEFAULT ? null : color;
    });

    /** Cor ao vivo vinda do seletor livre do painel (null = volta à salva). */
    public previewFill(color: string | null): void {
        this._liveFill.set(color);
    }

    /** Cor do fundo pelo painel (igual à do documento vira herança). */
    public setFill(color: string): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withMediaFill(field.configs, color, this.editDataService.document()?.config)
        );
    }

    /** Estilo da legenda efetivo (card, documento), como no PDF. */
    public captionStyle = computed<CaptionStyle>(() =>
        resolveCaptionStyle([
            this.getCurrentField()?.configs,
            this.editDataService.document()?.config,
        ])
    );

    /** Descrição ao lado efetiva (card, documento). */
    public captionSide = computed<boolean>(() =>
        resolveCaptionSide([this.getCurrentField()?.configs, this.editDataService.document()?.config])
    );

    /** Estilo da legenda pelo menu "Vídeo" (igual ao documento vira herança). */
    public setCaptionStyle(style: CaptionStyle): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withCaptionStyle(field.configs, style, this.editDataService.document()?.config)
        );
    }

    /** Descrição ao lado pelo menu "Vídeo" (igual ao documento vira herança). */
    public setCaptionSide(side: boolean): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withCaptionSide(field.configs, side, this.editDataService.document()?.config)
        );
    }

    /** Tamanho da miniatura (1 a 3): override do card, senão a config do documento. */
    public videoSize = computed<ImageSize>(() => ({
        grid:
            this.getCurrentField()?.configs?.singleVideoGrid ??
            this.editDataService.document()?.config?.singleVideoGrid ??
            1,
        width: null,
    }));

    public isFullImage = computed<boolean>(
        () =>
            this.getCurrentField()?.configs?.fullImage ??
            this.editDataService.document()?.config?.fullImage ??
            false
    );

    public showVideo = computed<boolean>(
        () =>
            this.getCurrentField()?.configs?.showVideo ??
            this.editDataService.document()?.config?.showVideo ??
            true
    );

    /** Algo do menu "Vídeo" está sobrescrito neste card (mostra "Voltar ao padrão"). */
    public lookOverridden = computed(() =>
        hasAnyKey(this.getCurrentField()?.configs, VideoCardComponent.LOOK_KEYS)
    );

    /** Tamanho da miniatura deste vídeo (igual ao documento vira herança). */
    public setVideoGrid(grid: number): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        const inherited = this.editDataService.document()?.config?.singleVideoGrid ?? 1;
        this.editItemsService.updateItemConfig(
            field._id,
            withOverride(field.configs, 'singleVideoGrid', grid, inherited)
        );
    }

    /** Enquadramento e "mostrar no PDF" pelo menu "Vídeo" (igual ao documento vira herança). */
    public setLook(key: 'fullImage' | 'showVideo', value: boolean, fallback: boolean): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        const inherited = this.editDataService.document()?.config?.[key] ?? fallback;
        this.editItemsService.updateItemConfig(
            field._id,
            withOverride(field.configs, key, value, inherited)
        );
    }

    /** "Voltar ao padrão do documento": só as chaves do vídeo; o estilo do texto fica. */
    public restoreLook(): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this.editItemsService.updateItemConfig(
            field._id,
            withoutKeys(field.configs, VideoCardComponent.LOOK_KEYS)
        );
    }

    /**
     * Salva as configurações só deste card (auto-save do modal).
     * @param configs Override do item — o que não estiver aqui segue o documento
     */
    /**
     * O vídeo ainda está sendo normalizado no servidor.
     *
     * Só vale para o card do documento: na lixeira e nos favoritos o campo é uma cópia
     * congelada, e um selo de "processando" ali seria mentira — não há worker olhando para
     * aquele item.
     */
    public isProcessing(): boolean {
        if (this.type() !== CardTypes.DEFAULT) return false;

        return this.getCurrentField()?.videoStatus != null;
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }
}
